import { useState, useEffect, useMemo } from 'react';
import { 
  Moon, Flame, Brain, Activity, Plus, TrendingUp,
  Calendar, Clock, MapPin, Heart, Thermometer
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import AddTrackingModal from '../components/tracking/AddTrackingModal';
import { 
  getSleepLogs, getPainLogs, getStressLogs, getVitalsLogs 
} from '../services/advancedTrackingService';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'sleep' | 'pain' | 'stress' | 'vitals';

export default function AdvancedTrackingPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('sleep');
  const [sleepLogs, setSleepLogs] = useState<any[]>([]);
  const [painLogs, setPainLogs] = useState<any[]>([]);
  const [stressLogs, setStressLogs] = useState<any[]>([]);
  const [vitalsLogs, setVitalsLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // All available tabs
  const allTabs = [
    { id: 'sleep' as TabType, label: 'Somn', icon: Moon, color: 'indigo' },
    { id: 'pain' as TabType, label: 'Durere', icon: Flame, color: 'red' },
    { id: 'stress' as TabType, label: 'Stres', icon: Brain, color: 'orange' },
    { id: 'vitals' as TabType, label: 'Vitale', icon: Activity, color: 'emerald' },
  ];

  // Filter tabs based on healthGoal - show only RELEVANT metrics!
  const tabs = useMemo(() => {
    if (!user?.healthGoal) return allTabs; // Show all if no goal set

    // Map healthGoal -> relevant tracking metrics
    const goalToMetrics: Record<string, TabType[]> = {
      // HEALTHY MODE - Focus on performance & wellness
      WEIGHT_LOSS: ['sleep', 'stress'],
      MUSCLE_GAIN: ['sleep', 'stress'],
      HEALTHY_LIFESTYLE: ['sleep', 'stress'],
      REDUCE_BELLY_FAT: ['sleep', 'stress'],
      INCREASE_ENERGY: ['sleep', 'stress'],
      CLEAN_EATING: ['sleep', 'stress'],
      REDUCE_SUGAR_CARBS: ['vitals', 'sleep', 'stress'], // Track glucose if available
      BETTER_SLEEP: ['sleep', 'stress'], // ⭐ FOCUS: Sleep quality
      REDUCE_BLOATING: ['sleep', 'stress'],
      START_EXERCISE: ['sleep', 'stress'],
      IMPROVE_FOCUS: ['sleep', 'stress'],
      MAINTAIN_WEIGHT: ['sleep', 'stress'],
      
      // CHRONIC MODE - Track pain, vitals, and health markers
      EASY_STANDING: ['pain', 'vitals', 'stress'],
      REDUCE_INFLAMMATION: ['pain', 'vitals', 'sleep', 'stress'],
      RECOVER_MUSCLE_STRENGTH: ['pain', 'sleep', 'stress'],
      CLIMB_STAIRS: ['pain', 'vitals', 'stress'],
      REDUCE_JOINT_PAIN: ['pain', 'sleep', 'stress'], // ⭐ FOCUS: Pain tracking
      BREATHE_BETTER: ['vitals', 'stress', 'sleep'],
      STABILIZE_GLUCOSE: ['vitals', 'sleep', 'stress'], // ⭐ FOCUS: Glucose tracking
      IMPROVE_BALANCE: ['pain', 'stress'],
      REDUCE_CHRONIC_FATIGUE: ['sleep', 'vitals', 'stress'],
      IMPROVE_DIGESTION: ['pain', 'sleep', 'stress'],
      REGAIN_MOBILITY: ['pain', 'sleep', 'stress'],
      PREVENT_DISEASE_WORSENING: ['pain', 'vitals', 'sleep', 'stress'],
      HEALTHY_WEIGHT_LOSS_CHRONIC: ['sleep', 'stress'],
      CONTROL_CHRONIC_PAIN: ['pain', 'vitals', 'sleep', 'stress'], // ⭐ FOCUS: Pain + meds tracking
      IMPROVE_LIFE_WITH_DISEASE: ['pain', 'vitals', 'sleep', 'stress']
    };

    const relevantMetrics = goalToMetrics[user.healthGoal];
    if (!relevantMetrics) return allTabs; // Fallback to all

    // Filter tabs to show only relevant ones
    return allTabs.filter(tab => relevantMetrics.includes(tab.id));
  }, [user?.healthGoal]);

  // Set default active tab to first available tab
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      switch (activeTab) {
        case 'sleep':
          const sleepData = await getSleepLogs({ 
            startDate: thirtyDaysAgo.toISOString(),
            limit: 30 
          });
          setSleepLogs(sleepData.logs || []);
          break;
        case 'pain':
          const painData = await getPainLogs({ 
            startDate: thirtyDaysAgo.toISOString(),
            limit: 30 
          });
          setPainLogs(painData.logs || []);
          break;
        case 'stress':
          const stressData = await getStressLogs({ 
            startDate: thirtyDaysAgo.toISOString(),
            limit: 30 
          });
          setStressLogs(stressData.logs || []);
          break;
        case 'vitals':
          const vitalsData = await getVitalsLogs({ 
            startDate: thirtyDaysAgo.toISOString(),
            limit: 30 
          });
          setVitalsLogs(vitalsData.logs || []);
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLogs = () => {
    switch (activeTab) {
      case 'sleep': return sleepLogs;
      case 'pain': return painLogs;
      case 'stress': return stressLogs;
      case 'vitals': return vitalsLogs;
      default: return [];
    }
  };

  const getTabColor = (tabId: TabType) => {
    const tab = tabs.find(t => t.id === tabId);
    return tab?.color || 'indigo';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="Tracking Avansat" 
        subtitle="Monitorizează somn, durere, stres și vitale"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Card */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 mb-6 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Tracking Complet</h3>
              <p className="text-indigo-100 text-sm leading-relaxed">
                Monitorizează aspecte esențiale ale sănătății tale: calitatea somnului, 
                intensitatea durerii, nivelul de stres și parametrii vitali.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const borderClass = index < 2 ? 'border-b' : '';
              const rightBorderClass = (index % 2 === 0 && index < 3) || (index === 0) ? 'md:border-r' : '';
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative p-4 flex flex-col items-center gap-2 transition-all
                    ${borderClass} ${rightBorderClass} border-gray-200
                    ${isActive 
                      ? `bg-${tab.color}-50 text-${tab.color}-600` 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                    ${index === 0 ? 'rounded-tl-2xl' : ''}
                    ${index === 1 ? 'md:rounded-tr-none rounded-tr-2xl' : ''}
                    ${index === 3 ? 'rounded-br-2xl' : ''}
                    ${index === 2 ? 'md:rounded-bl-none rounded-bl-2xl' : ''}
                  `}
                >
                  {isActive && (
                    <div className={`absolute inset-x-0 bottom-0 h-1 bg-${tab.color}-500`} />
                  )}
                  <Icon className={`w-6 h-6 ${isActive ? `text-${tab.color}-600` : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${isActive ? `text-${tab.color}-700` : ''}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          {/* Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Ultimele Înregistrări
            </h2>
            <button 
              onClick={() => setIsModalOpen(true)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white transition-colors shadow-sm
                ${activeTab === 'sleep' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                ${activeTab === 'pain' ? 'bg-red-600 hover:bg-red-700' : ''}
                ${activeTab === 'stress' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                ${activeTab === 'vitals' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              `}
            >
              <Plus className="w-4 h-4" />
              Adaugă
            </button>
          </div>

          {/* Logs List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              Se încarcă...
            </div>
          ) : getCurrentLogs().length === 0 ? (
            <div className="text-center py-12">
              <div className={`inline-flex p-4 rounded-full bg-${getTabColor(activeTab)}-100 mb-4`}>
                {activeTab === 'sleep' && <Moon className={`w-8 h-8 text-${getTabColor(activeTab)}-600`} />}
                {activeTab === 'pain' && <Flame className={`w-8 h-8 text-${getTabColor(activeTab)}-600`} />}
                {activeTab === 'stress' && <Brain className={`w-8 h-8 text-${getTabColor(activeTab)}-600`} />}
                {activeTab === 'vitals' && <Activity className={`w-8 h-8 text-${getTabColor(activeTab)}-600`} />}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nicio înregistrare încă
              </h3>
              <p className="text-gray-500 mb-4">
                Începe să urmărești {tabs.find(t => t.id === activeTab)?.label.toLowerCase()}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {getCurrentLogs().map((log: any) => (
                <LogCard 
                  key={log.id} 
                  log={log} 
                  type={activeTab}
                  color={getTabColor(activeTab)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddTrackingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsModalOpen(false);
        }}
        type={activeTab}
      />
    </div>
  );
}

function LogCard({ log, type, color }: { log: any; type: TabType; color: string }) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderContent = () => {
    switch (type) {
      case 'sleep':
        const sleepQuality = log.quality === 'POOR' ? 'Slabă' : 
                           log.quality === 'FAIR' ? 'Acceptabilă' :
                           log.quality === 'GOOD' ? 'Bună' : 'Excelentă';
        return (
          <>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>{log.duration ? `${Number(log.duration).toFixed(1)}h` : 'N/A'}</span>
              <span className="mx-2">•</span>
              <span>{sleepQuality}</span>
              {log.interruptions !== undefined && log.interruptions !== null && (
                <>
                  <span className="mx-2">•</span>
                  <span>{log.interruptions} treziri</span>
                </>
              )}
            </div>
            {log.notes && <p className="text-sm text-gray-600 mt-2">{log.notes}</p>}
          </>
        );
      case 'pain':
        return (
          <>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Flame className="w-4 h-4" />
              <span>Nivel: {log.painLevel}/10</span>
              {log.location && log.location.length > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <MapPin className="w-4 h-4" />
                  <span>{log.location[0]}</span>
                </>
              )}
            </div>
            {log.notes && <p className="text-sm text-gray-600 mt-2">{log.notes}</p>}
          </>
        );
      case 'stress':
        const stressMood = log.mood === 'VERY_GOOD' ? 'Foarte bine' :
                          log.mood === 'GOOD' ? 'Bine' :
                          log.mood === 'NEUTRAL' ? 'Neutru' :
                          log.mood === 'BAD' ? 'Rău' : 'Foarte rău';
        return (
          <>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Brain className="w-4 h-4" />
              <span>Nivel: {log.stressLevel}/10</span>
              <span className="mx-2">•</span>
              <span>{stressMood}</span>
            </div>
            {log.notes && <p className="text-sm text-gray-600 mt-2">{log.notes}</p>}
          </>
        );
      case 'vitals':
        const hasData = log.glucose || log.systolicBP || log.heartRate || log.temperature;
        if (!hasData) {
          return <p className="text-sm text-gray-500 italic">Nu există date înregistrate</p>;
        }
        return (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {log.glucose && (
              <div className="flex items-center gap-2 text-gray-600">
                <Activity className="w-4 h-4" />
                <span>Glicemie: {Number(log.glucose).toFixed(1)} mg/dL</span>
              </div>
            )}
            {(log.systolicBP || log.diastolicBP) && (
              <div className="flex items-center gap-2 text-gray-600">
                <Heart className="w-4 h-4" />
                <span>TA: {log.systolicBP || '-'}/{log.diastolicBP || '-'}</span>
              </div>
            )}
            {log.heartRate && (
              <div className="flex items-center gap-2 text-gray-600">
                <Activity className="w-4 h-4" />
                <span>Puls: {log.heartRate} bpm</span>
              </div>
            )}
            {log.temperature && (
              <div className="flex items-center gap-2 text-gray-600">
                <Thermometer className="w-4 h-4" />
                <span>Temp: {Number(log.temperature).toFixed(1)}°C</span>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className={`p-4 rounded-xl border-2 border-${color}-100 bg-${color}-50/50 hover:border-${color}-200 transition-colors`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <Calendar className={`w-4 h-4 text-${color}-600`} />
          <span className="text-sm font-medium text-gray-900">
            {formatDate(log.date || log.createdAt)}
          </span>
        </div>
      </div>
      {renderContent()}
    </div>
  );
}
